import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaClipboardList, FaEdit, FaEye, FaFilter, FaLock, FaSearch, FaTimes } from 'react-icons/fa';
import * as orderService from '../../services/order.service';
import { countryName } from '../../components/BuyerLedger/buyerLedgerCountries';
import PageHeader from '../../components/PageHeader/PageHeader';
import { useAuth } from '../../context/AuthContext';
import '../ClientCatalog/ClientCatalog.css';

const INITIAL_MODAL_STATE = { open: false, order: null, action: 'review' };

const isAuthorized = (status) => (status || '').toLowerCase() === 'authorized';

// An authorized order is locked: its details cannot be edited until it is un-authorized.
const LOCKED_MESSAGE = 'Product details are locked for editing once an order has been authorized.';

const fmtDate = (d) => (d ? d : '—');
const fmtQty = (n) => Number(n || 0).toLocaleString();

const OrderAuthorization = () => {
  const navigate = useNavigate();
  const { hasModuleAccess } = useAuth();
  const canAuthorize = hasModuleAccess('authorize_action');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(INITIAL_MODAL_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadOrders = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const data = await orderService.getOrders();
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load orders for authorization', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders(true);
  }, [loadOrders]);

  // Refresh on window focus so edits made on the order form show up when the user returns.
  useEffect(() => {
    const handleFocus = () => loadOrders(false);
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadOrders]);

  const handleAuthorize = async (decision) => {
    if (!modal.order?.id) return;
    setSubmitting(true);
    try {
      const nextStatus = decision === 'authorize' ? 'authorized' : 'pending';
      const updated = await orderService.updateOrderAuthorization(modal.order.id, nextStatus);
      setOrders((prev) =>
        prev.map((o) => (o.id === modal.order.id ? { ...o, status: updated?.status ?? nextStatus } : o)),
      );
      setModal(INITIAL_MODAL_STATE);
    } catch (error) {
      console.error('Failed to change order authorization', error);
      alert('Failed to update order authorization status');
    } finally {
      setSubmitting(false);
    }
  };

  const visibleOrders = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return orders.filter((o) => {
      const status = isAuthorized(o.status) ? 'authorized' : 'pending';
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (!q) return true;
      const haystack = [o.orderNo, o.orderType, o.buyerGroup, o.buyerName, o.buyerPo, countryName(o.country), status]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [orders, searchTerm, statusFilter]);

  const authorizedCount = orders.filter((o) => isAuthorized(o.status)).length;

  return (
    <div className="client-catalog-container authorization-page" style={{ minHeight: '100vh' }}>
      <PageHeader
        icon={<FaClipboardList size={22} />}
        title="Order Authorize"
        subtitle="Review orders, authorize them, or edit the order form before approval"
      />

      <div className="client-catalog-main">
        <section className="catalog-content-area-full">
          <div className="authorization-summary-row">
            <div className="authorization-summary-card">
              <span>Total orders</span>
              <strong>{orders.length}</strong>
            </div>
            <div className="authorization-summary-card">
              <span>Authorized</span>
              <strong>{authorizedCount}</strong>
            </div>
            <div className="authorization-summary-card">
              <span>Pending</span>
              <strong>{orders.length - authorizedCount}</strong>
            </div>
          </div>

          <div className="authorization-toolbar">
            <label className="authorization-search">
              <FaSearch className="authorization-search-icon" />
              <input
                type="text"
                placeholder="Search order no., buyer, PO, country..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </label>

            <label className="authorization-filter">
              <FaFilter className="authorization-search-icon" />
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="all">All statuses</option>
                <option value="authorized">Authorized</option>
                <option value="pending">Pending</option>
              </select>
            </label>
          </div>

          {loading ? (
            <div className="empty-state authorization-empty-state">
              <p>Loading orders...</p>
            </div>
          ) : visibleOrders.length === 0 ? (
            <div className="empty-state authorization-empty-state">
              <FaClipboardList size={48} className="text-muted mb-3" />
              <h5>No matching orders</h5>
              <p className="text-muted">Try adjusting the search term or status filter.</p>
            </div>
          ) : (
            <div className="authorization-table-card">
              <div className="table-responsive">
                <table className="table authorization-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Order Type</th>
                      <th>Order No.</th>
                      <th>Order Recv. Dt.</th>
                      <th>Ex Factory Date</th>
                      <th>Order Qty</th>
                      <th>Buyer Name</th>
                      <th>Buyer PO No.</th>
                      <th>Country</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleOrders.map((order, index) => {
                      const authorized = isAuthorized(order.status);
                      return (
                        <tr key={order.id}>
                          <td>{index + 1}</td>
                          <td>{order.orderType || '—'}</td>
                          <td>{order.orderNo || '—'}</td>
                          <td>{fmtDate(order.recDate)}</td>
                          <td>{fmtDate(order.exFactoryDate)}</td>
                          <td>{fmtQty(order.orderQty)}</td>
                          <td>{order.buyerName || '—'}</td>
                          <td>{order.buyerPo || '—'}</td>
                          <td>{countryName(order.country) || 'India'}</td>
                          <td>
                            <span className={`authorization-status-pill ${authorized ? 'approved' : 'unapproved'}`}>
                              {authorized ? 'Authorized' : 'Pending'}
                            </span>
                          </td>
                          <td>
                            <div className="authorization-action-group">
                              {authorized ? (
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  disabled
                                  title={LOCKED_MESSAGE}
                                >
                                  <FaLock className="me-1" /> Edit
                                </button>
                              ) : (
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => navigate(`/dashboard/order/edit/${order.id}`)}
                                >
                                  <FaEdit className="me-1" /> Edit
                                </button>
                              )}
                              {canAuthorize &&
                                (authorized ? (
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => setModal({ open: true, order, action: 'unauthorize' })}
                                  >
                                    <FaTimes className="me-1" /> Unauthorize
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-outline-success"
                                    onClick={() => setModal({ open: true, order, action: 'authorize' })}
                                  >
                                    <FaCheck className="me-1" /> Authorize
                                  </button>
                                ))}
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setModal({ open: true, order, action: 'review' })}
                              >
                                <FaEye className="me-1" /> Review
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>

      {modal.open && modal.order && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="import-modal" style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <div>
                <h5>
                  {modal.action === 'authorize'
                    ? 'Authorize order'
                    : modal.action === 'unauthorize'
                      ? 'Unauthorize order'
                      : 'Order details'}
                </h5>
                <p className="text-muted mb-0">{modal.order.orderNo} • {modal.order.buyerName}</p>
              </div>
              <button className="btn-close-client" onClick={() => setModal(INITIAL_MODAL_STATE)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              <ul className="list-group">
                <li className="list-group-item"><strong>Order No.:</strong> {modal.order.orderNo || '—'}</li>
                <li className="list-group-item"><strong>Order Type:</strong> {modal.order.orderType || '—'}</li>
                <li className="list-group-item"><strong>Buyer:</strong> {modal.order.buyerName || '—'}</li>
                <li className="list-group-item"><strong>Buyer PO No.:</strong> {modal.order.buyerPo || '—'}</li>
                <li className="list-group-item"><strong>Order Recv. Dt.:</strong> {fmtDate(modal.order.recDate)}</li>
                <li className="list-group-item"><strong>Ex Factory Date:</strong> {fmtDate(modal.order.exFactoryDate)}</li>
                <li className="list-group-item"><strong>Order Qty:</strong> {fmtQty(modal.order.orderQty)}</li>
                <li className="list-group-item"><strong>Country:</strong> {countryName(modal.order.country) || 'India'}</li>
                <li className="list-group-item"><strong>Grand Total:</strong> {modal.order.currency ? `${modal.order.currency} ` : ''}{Number(modal.order.grandTotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</li>
                <li className="list-group-item"><strong>Status:</strong> {isAuthorized(modal.order.status) ? 'Authorized' : 'Pending'}</li>
              </ul>
              {modal.action === 'review' &&
                (isAuthorized(modal.order.status) ? (
                  <div className="alert alert-secondary d-flex align-items-center gap-2 mt-3 mb-0 py-2 px-3">
                    <FaLock /> <span>{LOCKED_MESSAGE}</span>
                  </div>
                ) : (
                  <button
                    className="btn btn-outline-primary w-100 mt-3"
                    onClick={() => navigate(`/dashboard/order/edit/${modal.order.id}`)}
                  >
                    <FaEdit className="me-1" /> Edit
                  </button>
                ))}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={() => setModal(INITIAL_MODAL_STATE)}>
                {modal.action === 'review' ? 'Close' : 'Cancel'}
              </button>
              {(modal.action === 'authorize' || modal.action === 'unauthorize') && (
                <button
                  className={modal.action === 'authorize' ? 'btn btn-success' : 'btn btn-danger'}
                  onClick={() => handleAuthorize(modal.action)}
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : modal.action === 'authorize' ? 'Authorize' : 'Unauthorize'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderAuthorization;
